export type BoardListColumnId =
    | 'change'
    | 'project'
    | 'column'
    | 'assignee'
    | 'status'
    | 'estimate'
    | 'deadline'
    | 'importance'
    | 'priority'
    | 'client'
    | 'requester'
    | 'updatedAt';

export const BOARD_LIST_ALL_COLUMNS: BoardListColumnId[] = [
    'change',
    'project',
    'column',
    'assignee',
    'status',
    'estimate',
    'deadline',
    'importance',
    'priority',
    'client',
    'requester',
    'updatedAt',
];

/** Columns offered in the picker (change is always visible). */
export const BOARD_LIST_CONFIGURABLE_COLUMNS: BoardListColumnId[] = [
    'project',
    'column',
    'assignee',
    'status',
    'estimate',
    'deadline',
    'importance',
    'priority',
    'client',
    'requester',
    'updatedAt',
];

/** Default visible columns — column / importance / priority hidden. */
export const BOARD_LIST_DEFAULT_COLUMNS: BoardListColumnId[] = [
    'change',
    'project',
    'assignee',
    'status',
    'estimate',
    'deadline',
    'client',
    'requester',
    'updatedAt',
];

export type BoardListViewPrefs = {
    columns: BoardListColumnId[];
};

export type BoardListPrefsScope = {
    userId?: string | null;
    projectId?: string | null;
    boardId?: string | null;
};

const STORAGE_PREFIX = 'yoda.board.listViewPrefs.v2';
const LEGACY_STORAGE_KEY = 'yoda.board.listViewPrefs.v1';

export const DEFAULT_BOARD_LIST_VIEW_PREFS: BoardListViewPrefs = {
    columns: [...BOARD_LIST_DEFAULT_COLUMNS],
};

const isColumnId = (value: unknown): value is BoardListColumnId =>
    typeof value === 'string' && (BOARD_LIST_ALL_COLUMNS as string[]).includes(value);

const normalizeColumns = (columns: unknown): BoardListColumnId[] => {
    if (!Array.isArray(columns))
        return [...DEFAULT_BOARD_LIST_VIEW_PREFS.columns];

    const seen = new Set<BoardListColumnId>();
    const ordered: BoardListColumnId[] = [];
    for (const item of columns) {
        if (!isColumnId(item) || seen.has(item))
            continue;
        seen.add(item);
        ordered.push(item);
    }

    if (!ordered.length)
        return [...DEFAULT_BOARD_LIST_VIEW_PREFS.columns];

    if (!ordered.includes('change'))
        ordered.unshift('change');

    return ordered;
};

export function boardListPrefsStorageKey(scope: BoardListPrefsScope): string | null {
    const userId = scope.userId || '';
    const projectId = scope.projectId || '';
    const boardId = scope.boardId || '';
    if (!userId || !projectId || !boardId)
        return null;
    return `${STORAGE_PREFIX}:${userId}:${projectId}:${boardId}`;
}

export function loadBoardListViewPrefs(scope: BoardListPrefsScope): BoardListViewPrefs {
    try {
        const key = boardListPrefsStorageKey(scope);
        if (!key) {
            return {
                columns: [...DEFAULT_BOARD_LIST_VIEW_PREFS.columns],
            };
        }

        let raw = localStorage.getItem(key);
        // One-time fallback: seed from legacy global prefs if scoped key is empty.
        if (!raw)
            raw = localStorage.getItem(LEGACY_STORAGE_KEY);

        if (!raw) {
            return {
                columns: [...DEFAULT_BOARD_LIST_VIEW_PREFS.columns],
            };
        }
        const parsed = JSON.parse(raw);
        return {
            columns: normalizeColumns(parsed?.columns),
        };
    } catch {
        return {
            columns: [...DEFAULT_BOARD_LIST_VIEW_PREFS.columns],
        };
    }
}

export function saveBoardListViewPrefs(scope: BoardListPrefsScope, prefs: BoardListViewPrefs): void {
    try {
        const key = boardListPrefsStorageKey(scope);
        if (!key)
            return;
        localStorage.setItem(key, JSON.stringify({
            columns: normalizeColumns(prefs.columns),
        }));
    } catch {
        // Ignore quota / private mode failures.
    }
}

export function toggleBoardListColumn(
    columns: BoardListColumnId[],
    columnId: BoardListColumnId,
    visible: boolean,
): BoardListColumnId[] {
    if (columnId === 'change')
        return normalizeColumns(columns);

    if (visible) {
        if (columns.includes(columnId))
            return normalizeColumns(columns);

        // Insert at canonical position so re-enabled columns keep a stable order.
        const next = [...normalizeColumns(columns)];
        const canonicalIndex = BOARD_LIST_ALL_COLUMNS.indexOf(columnId);
        let insertAt = next.length;
        for (let i = 0; i < next.length; i++) {
            if (BOARD_LIST_ALL_COLUMNS.indexOf(next[i]) > canonicalIndex) {
                insertAt = i;
                break;
            }
        }
        next.splice(insertAt, 0, columnId);
        return normalizeColumns(next);
    }

    return normalizeColumns(columns.filter((id) => id !== columnId));
}

export function moveBoardListColumn(
    columns: BoardListColumnId[],
    columnId: BoardListColumnId,
    direction: 'up' | 'down',
): BoardListColumnId[] {
    const ordered = normalizeColumns(columns);
    const index = ordered.indexOf(columnId);
    if (index < 0 || columnId === 'change')
        return ordered;

    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= ordered.length)
        return ordered;
    // Keep change pinned first.
    if (ordered[swapWith] === 'change')
        return ordered;

    const next = [...ordered];
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    return normalizeColumns(next);
}
